import React, { useState, useRef } from 'react';

const TERMS_OF_SERVICE = `TIMBER SUMMIT EXPO — TERMS OF SERVICE

Last Updated: June 18, 2026

1. AGREEMENT TO TERMS

By creating an account on Timber Summit Expo ("Platform"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree, do not create an account or use the Platform.

2. PLATFORM DESCRIPTION

Timber Summit Expo is a subscription-based marketplace connecting artisans and makers ("Vendors") with buyers ("Customers"). The Platform facilitates transactions, communications, and content sharing between parties.

3. ACCOUNT ELIGIBILITY

You must be at least 18 years of age to create an account. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.

4. VENDOR TERMS

4.1 Approval. All Vendor accounts require administrative approval. The Platform reserves the right to approve, reject, suspend, or revoke Vendor access at its sole discretion.

4.2 Payments. Vendors must complete Stripe Connect onboarding to receive payments. Platform transaction fees are determined by the Vendor's subscription tier: Starter (8%), Pro (4%), or Elite (0%). These fees are in addition to Stripe's standard processing fees (approximately 2.9% + $0.30 per transaction).

4.3 Subscription Plans. Vendor plans are billed monthly. Cancellation takes effect at the end of the billing period. Downgrades may reduce listing limits and feature access.

4.4 Shipping. Vendors are solely responsible for fulfilling orders, providing accurate tracking information, and ensuring safe delivery. The Platform is not liable for lost, damaged, or delayed shipments.

4.5 Item Accuracy. Vendors must accurately represent their items. Misleading descriptions, photos, or claims may result in suspension or termination of the Vendor account.

4.6 Made-to-Order Items. Made-to-order items are charged immediately upon purchase. Vendors agree to fulfill within the processing time stated at listing. Failure to deliver within the stated timeframe entitles the Customer to cancel and receive a full refund.

5. CUSTOMER TERMS

5.1 Subscriptions. Customer access tiers (Explorer, Member, Insider) are billed monthly via Stripe. You may cancel at any time through the billing portal. Access continues until the end of the paid period.

5.2 Purchases. All purchases are made directly between Customers and Vendors. The Platform facilitates the transaction but is not a party to the sale. Prices are set by individual Vendors.

5.3 Offers. Customers may submit offers on items. Vendors may accept or decline at their discretion. Accepted offers create a binding obligation to pay.

5.4 Commissions. Commission requests are non-binding until a pitch is accepted and payment is completed. The Platform is not responsible for the quality or timeliness of commissioned work.

5.5 Cancellations. Orders without tracking may be cancelled immediately. Orders with tracking require Vendor approval. Made-to-order items may be cancelled within 24 hours unless the Vendor has begun production.

5.6 Refunds. Refund requests are reviewed by Platform administrators. Approved refunds are processed via Stripe within 5–10 business days. The Platform reserves the right to deny refunds at its discretion.

6. WORKSHOP (BLUEPRINT EXCHANGE)

6.1 Content Ownership. Creators retain ownership of blueprints they publish. By publishing, you grant the Platform a non-exclusive license to display and distribute your content within the Platform.

6.2 Forking. Published blueprints may be forked (modified and republished) by other users in accordance with the license selected by the original creator. Attribution to the original creator is permanent and immutable.

6.3 Licenses. MIT-licensed blueprints may be freely modified and sold. CC-BY blueprints require attribution. Protected blueprints are for personal use only and may not be sold or redistributed.

7. FEES AND PRICING

7.1 The Platform reserves the right to modify subscription prices, transaction fees, and feature availability with 30 days written notice to affected users.

7.2 All prices displayed to Customers are final. Platform fees are deducted from Vendor proceeds and are not charged to Customers.

8. PROHIBITED CONDUCT

Users may not: (a) submit false information; (b) harass, threaten, or abuse other users; (c) upload malicious content; (d) circumvent payment systems; (e) create multiple accounts to evade restrictions; (f) use the Platform for illegal purposes.

9. TERMINATION

The Platform may suspend or terminate accounts at its sole discretion for violation of these terms, fraudulent activity, or any other reason deemed appropriate. Suspended vendors retain their data; banned vendors have their content permanently removed.

10. LIMITATION OF LIABILITY

The Platform is provided "as is" without warranty. Timber Summit Expo shall not be liable for indirect, incidental, special, or consequential damages arising from use of the Platform, including but not limited to lost profits, lost data, or business interruption.

11. DISPUTE RESOLUTION

Disputes between Vendors and Customers should first be addressed between the parties. The Platform may mediate at its discretion but is not obligated to do so. All disputes with the Platform shall be resolved through binding arbitration.

12. GOVERNING LAW

These Terms shall be governed by the laws of the United States. Any legal action shall be brought in the applicable federal or state courts.

13. CHANGES TO TERMS

We may update these Terms at any time. Material changes will be communicated via email. Continued use of the Platform after changes constitutes acceptance.`;

const TERMS_OF_USE = `TIMBER SUMMIT EXPO — TERMS OF USE (ACCEPTABLE USE POLICY)

Last Updated: June 18, 2026

1. PURPOSE

This Acceptable Use Policy governs your behavior on Timber Summit Expo. All users—Vendors, Customers, and visitors—must comply with these terms.

2. CONTENT STANDARDS

All content uploaded to the Platform (including item listings, descriptions, photos, blueprints, comments, and commission requests) must:

(a) Be accurate and not misleading.
(b) Not infringe upon any third party's intellectual property rights.
(c) Not contain hateful, discriminatory, or threatening language.
(d) Not contain obscene, pornographic, or sexually explicit material.
(e) Not promote violence, illegal activity, or self-harm.
(f) Not contain personal information of third parties without consent.

3. INTELLECTUAL PROPERTY

3.1 You represent that you own or have proper licenses for all content you upload. The Platform is not responsible for verifying ownership claims.

3.2 If you believe your intellectual property has been infringed, contact us with details of the alleged infringement. We will investigate and take appropriate action.

4. COMMUNICATION STANDARDS

All communications through the Platform (commission discussions, offer negotiations, reviews) must be professional and respectful. Harassment, threats, spam, or solicitation of off-platform transactions is prohibited.

5. FAIR USE OF PLATFORM FEATURES

5.1 Do not manipulate the Spotlight Bid system through shill bidding or bid manipulation.
5.2 Do not create fake accounts to inflate reviews, star counts, or contribution metrics.
5.3 Do not spam the Commission Board or Workshop with duplicate or low-quality content.
5.4 Do not abuse the offer system to harass Vendors with unreasonably low offers.
5.5 Do not attempt to circumvent subscription paywalls or access restrictions.

6. VENDOR-SPECIFIC OBLIGATIONS

6.1 Respond to orders within a reasonable timeframe.
6.2 Ship items using trackable methods when possible.
6.3 Accurately represent processing times for made-to-order items.
6.4 Do not list items you cannot fulfill.
6.5 Maintain active communication with Customers regarding their orders.

7. CUSTOMER-SPECIFIC OBLIGATIONS

7.1 Provide accurate shipping information.
7.2 Do not file fraudulent refund claims.
7.3 Do not use chargebacks as a first resort—use the Platform's refund system.
7.4 Respect Vendor processing times for custom and made-to-order work.

8. ENFORCEMENT

Violations may result in: (a) content removal; (b) account suspension; (c) permanent ban; (d) forfeiture of pending payments. The severity of action is at the Platform's sole discretion based on the nature and frequency of violations.

9. REPORTING VIOLATIONS

If you observe a violation of this policy, contact the Platform administrators. We will review all reports and take appropriate action. False or malicious reports may themselves constitute a violation.

10. PRIVACY

Your use of the Platform is also governed by our Privacy Policy. By using the Platform, you consent to the collection and use of information as described therein.`;

function TermsAcceptance({ onAccept, onCancel }) {
  const [step, setStep] = useState(0); // 0 = ToS, 1 = ToU
  const [tosScrolled, setTosScrolled] = useState(false);
  const [touScrolled, setTouScrolled] = useState(false);
  const scrollRef = useRef(null);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    if (step === 0 && atBottom) setTosScrolled(true);
    if (step === 1 && atBottom) setTouScrolled(true);
  }

  return (
    <div className="item-form-overlay">
      <div className="item-form terms-modal">
        <h3>{step === 0 ? 'Terms of Service' : 'Acceptable Use Policy'}</h3>
        <p className="settings-hint">{step === 0 ? 'Step 1 of 2 — Please read and scroll to the bottom' : 'Step 2 of 2 — Please read and scroll to the bottom'}</p>
        <div className="terms-scroll-box" ref={scrollRef} onScroll={handleScroll}>
          <pre className="terms-text">{step === 0 ? TERMS_OF_SERVICE : TERMS_OF_USE}</pre>
        </div>
        <div className="form-buttons">
          {step === 0 && (
            <button className="btn-primary" disabled={!tosScrolled} onClick={() => { setStep(1); if (scrollRef.current) scrollRef.current.scrollTop = 0; }}>
              {tosScrolled ? 'Accept & Continue' : 'Scroll to bottom to continue'}
            </button>
          )}
          {step === 1 && (
            <button className="btn-primary" disabled={!touScrolled} onClick={onAccept}>
              {touScrolled ? 'Accept & Create Account' : 'Scroll to bottom to continue'}
            </button>
          )}
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default TermsAcceptance;
